/**
 * Typed client for the WaveQA API (spec 73.11: typed contracts between frontend
 * and backend).
 *
 * In development requests go to a same-origin `/api` path, which Vite proxies
 * to the local API. Production serves the API from its own origin
 * (`api.waveqa-agent.com`), so the base is supplied at build time by
 * `VITE_API_BASE_URL` — see `.env.production`.
 *
 * That makes every call cross-origin, which has two consequences the API
 * already accounts for: the origin must be named in `WAVE_CORS_ALLOWED_ORIGINS`,
 * and requests must opt in to sending the refresh cookie (see `credentials`
 * below). `api.` and the apex share the registrable domain `waveqa-agent.com`,
 * so the cookie's `SameSite=Strict` is still satisfied — SameSite compares
 * sites, not origins.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  /** Per-field validation messages, when the API rejected the shape. */
  readonly fields?: Record<string, string[]>;

  // Fields are assigned explicitly rather than declared as constructor
  // parameter properties: `erasableSyntaxOnly` is on, so the build must be able
  // to strip types without rewriting behaviour, and parameter properties emit
  // assignments that type-erasure alone cannot reproduce.
  constructor(
    message: string,
    status: number,
    code?: string,
    fields?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

interface ProblemDetails {
  title?: string;
  detail?: string;
  code?: string;
  fields?: Record<string, string[]>;
}

/**
 * The access token lives here — a module-level variable, deliberately not
 * `localStorage`.
 *
 * A token in localStorage is readable by any script on the page, so a single
 * XSS bug hands an attacker a working session. Held in memory it dies with the
 * tab, and the long-lived refresh token stays in an HttpOnly cookie that
 * JavaScript cannot touch at all.
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

interface RequestOptions extends RequestInit {
  /** Set for calls that should not attempt a token refresh on 401. */
  noRetry?: boolean;
}

async function request<T>(path: string, init: RequestOptions = {}): Promise<T> {
  const { noRetry, ...rest } = init;

  const response = await fetch(`${API_BASE}${path}`, {
    ...rest,
    // Always send the refresh cookie; the API scopes it to /api/v1/auth.
    // `include` rather than `same-origin`: production serves the API from
    // api.waveqa-agent.com, and `same-origin` omits cookies on a cross-origin
    // request — which would silently fail every refresh and sign users out
    // the moment their 15-minute access token expired.
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(rest.body ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...rest.headers,
    },
  });

  // One silent refresh attempt. Access tokens are short by design, so an
  // expired one during normal use is expected rather than exceptional.
  if (response.status === 401 && !noRetry && accessToken) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return request<T>(path, { ...init, noRetry: true });
    }
  }

  if (!response.ok) {
    // The API answers failures as ProblemDetails, but a proxy or network fault
    // can return HTML. Parsing defensively keeps a 502 from surfacing as an
    // unhelpful JSON syntax error.
    let problem: ProblemDetails = {};
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      /* non-JSON error body */
    }

    throw new ApiError(
      problem.detail ?? problem.title ?? `Request failed (${response.status})`,
      response.status,
      problem.code,
      problem.fields,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * The in-flight refresh, shared by every caller.
 *
 * Refresh tokens rotate: presenting one consumes it and issues a replacement.
 * The server treats a *second* presentation of an already-rotated token as
 * replay and revokes the whole session family — correctly, since that is what
 * a stolen token looks like.
 *
 * So the client must never present the same token twice concurrently. Two tabs
 * reloading together, a retry racing the initial restore, or React StrictMode
 * double-invoking an effect would each otherwise log the user out entirely.
 * Collapsing concurrent callers onto one promise makes that impossible.
 */
let refreshInFlight: Promise<TokenResponse> | null = null;

export async function refreshSession(): Promise<TokenResponse> {
  refreshInFlight ??= request<TokenResponse>('/auth/refresh', {
    method: 'POST',
    noRetry: true,
  }).finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

async function tryRefresh(): Promise<boolean> {
  try {
    const session = await refreshSession();
    setAccessToken(session.access_token);
    return true;
  } catch {
    setAccessToken(null);
    return false;
  }
}

// --- Contracts ------------------------------------------------------------

export interface Plan {
  plan: 'free' | 'basic' | 'standard' | 'premium';
  name: string;
  monthlyPriceMinorUnits: number | null;
  currency: string;
  agentLimit: number;
  monthlyHours: number;
  /** True when the plan is sold as unlimited hours; `monthlyHours` is then a sentinel. */
  unlimitedHours: boolean;
  contactSales: boolean;
}

export interface UserProfile {
  id: string;
  email: string;
  display_name: string | null;
  status: string;
  email_verified_at: string | null;
  last_login_at: string | null;
  time_zone: string;
  created_at: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: 'Bearer';
  expires_at: string;
  refresh_token: string | null;
  user: UserProfile;
}

export interface LoginChallenge {
  challenge_id: string;
  expires_at: string;
  otp_digits: number;
  message: string;
}

export interface MessageResponse {
  message: string;
}

export interface Subscription {
  plan: string;
  status: string;
  agent_limit: number;
  agents_in_use: number;
  monthly_hour_allowance: number;
  hours_used_this_period: number;
  hours_remaining: number;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at: string | null;
  is_entitled: boolean;
  /** Every run ever reported, not the capped page the activity list returns. */
  qa_runs_total: number;

  // --- Launch promotion ---------------------------------------------------
  //
  // Facts, not a rendered sentence, and the decision behind them is the
  // server's. This page used to decide for itself that paid plans were
  // unavailable, with a `const` in the source and a comment explaining how to
  // switch it back on; it went stale the moment the server side moved.

  /** True while this account may run any plan without paying. */
  promotion_active: boolean;
  /** When the window closes, or closed. Null if never enrolled. */
  promotion_ends_at: string | null;
  /** Whole days left, rounded up. Null when no window is open. */
  promotion_days_remaining: number | null;
  /** Paid plan, no open window, nothing paying for it. */
  requires_payment: boolean;
  /** Whether checkout can be started at all on this deployment. */
  payment_gateway_available: boolean;
  /** Whether selecting a paid plan would work right now. */
  can_select_paid_plans: boolean;
}

/** Where to send the customer to pay. Null url until a gateway is wired up. */
export interface Checkout {
  provider: string;
  checkout_url: string | null;
}

/**
 * A QA document template, as the dashboard lists it.
 *
 * `document` is the filename a project gets (`feature-spec.md`); `filename` is
 * the template's own name (`feature-spec-template.md`). The digest is what the
 * agent uses to tell whether its bundled copy is current.
 */
export interface TemplateSummary {
  document: string;
  filename: string;
  sizeBytes: number;
  sha256: string;
  /** Whether a project must contain this document to be verifiable. */
  required: boolean;
}

export interface TemplateContent extends TemplateSummary {
  content: string;
}

export interface Credential {
  id: string;
  name: string;
  client_id: string;
  secret_prefix: string;
  status: string;
  created_at: string;
  last_used_at: string | null;
  last_used_from_ip: string | null;
  /** Self-reported by the agent, so it identifies a machine but proves nothing. */
  machine_mac: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
}

/**
 * Outcome of a delete. A credential with metered runs behind it is revoked and
 * kept rather than erased, so the caller must check `deleted` before telling
 * the user the record is gone.
 */
export interface CredentialDeleted {
  deleted: boolean;
  usage_records: number;
  detail: string;
}

/** The only response that ever carries a client secret. */
export interface CredentialCreated {
  credential: Credential;
  client_secret: string;
  warning: string;
}

export interface QaRun {
  run_id: string;
  project_name: string;
  feature_name: string | null;
  status: string;
  started_at: string;
  completed_at: string | null;
  /** What the run was billed, from the usage record. Null while it is open. */
  billable_seconds: number | null;
  criteria_total: number;
  criteria_passed: number;
  criteria_failed: number;
  criteria_blocked: number;
  agent_version: string | null;
}

export const api = {
  /**
   * The plan catalogue, served from the same source the entitlement checks
   * use. Rendering prices from the API rather than a hardcoded array is what
   * stops the pricing page and the enforcement code drifting apart.
   */
  getPlans: () => request<Plan[]>('/meta/plans'),

  getVersion: () => request<{ service: string; version: string }>('/meta/version'),

  /**
   * The contact form. Unauthenticated, and rate-limited server-side — a 429
   * here is the expected answer to someone submitting repeatedly, not a fault,
   * so the page renders its message rather than a generic failure.
   */
  sendContactMessage: (body: {
    name: string;
    email: string;
    company?: string | null;
    topic: string;
    message: string;
  }) => request<MessageResponse>('/contact', { method: 'POST', body: JSON.stringify(body) }),

  // --- Authentication -----------------------------------------------------

  register: (body: {
    email: string;
    password: string;
    confirm_password: string;
    accept_terms: boolean;
    display_name?: string | null;
  }) => request<MessageResponse>('/auth/register', { method: 'POST', body: JSON.stringify(body) }),

  activate: (token: string) =>
    request<TokenResponse>('/auth/activate', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  login: (email: string, password: string) =>
    request<LoginChallenge>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  verifyOtp: (challenge_id: string, code: string) =>
    request<TokenResponse>('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ challenge_id, code }),
    }),

  resendOtp: (challenge_id: string) =>
    request<LoginChallenge>('/auth/resend-otp', {
      method: 'POST',
      body: JSON.stringify({ challenge_id }),
    }),

  forgotPassword: (email: string) =>
    request<MessageResponse>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  resetPassword: (body: { token: string; password: string; confirm_password: string }) =>
    request<MessageResponse>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  logout: () => request<MessageResponse>('/auth/logout', { method: 'POST' }),

  me: () => request<UserProfile>('/auth/me'),

  /** Always goes through the shared in-flight promise — see `refreshSession`. */
  refresh: refreshSession,

  changePassword: (body: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }) =>
    request<MessageResponse>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // --- Subscription -------------------------------------------------------

  getSubscription: () => request<Subscription>('/subscription'),

  changePlan: (plan: string) =>
    request<Subscription>('/subscription/change', {
      method: 'POST',
      body: JSON.stringify({ plan }),
    }),

  /**
   * Start paying for the plan already on the account.
   *
   * Answers 503 until a payment gateway is configured, which is every
   * deployment today. The dashboard calls it anyway and renders the refusal:
   * a client that has to know in advance which deployments have billing is a
   * client that will be wrong about it.
   */
  startCheckout: () => request<Checkout>('/subscription/checkout', { method: 'POST' }),

  getRuns: () => request<QaRun[]>('/subscription/usage'),

  // --- QA document templates ----------------------------------------------
  //
  // Public endpoints: blank forms, served without a credential. `request`
  // still attaches the access token when there is one, which is harmless.

  getTemplates: () => request<TemplateSummary[]>('/templates'),

  getTemplate: (filename: string) =>
    request<TemplateContent>(`/templates/${encodeURIComponent(filename)}`),

  // --- Agent credentials --------------------------------------------------

  getCredentials: () => request<Credential[]>('/agents/credentials'),

  createCredential: (name: string) =>
    request<CredentialCreated>('/agents/credentials', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  // Rotating revokes the old secret immediately, so an agent still holding it
  // stops at its next call. No body: there is nothing left to configure.
  rotateCredential: (id: string) =>
    request<CredentialCreated>(`/agents/credentials/${id}/rotate`, {
      method: 'POST',
    }),

  revokeCredential: (id: string, reason?: string) =>
    request<Credential>(`/agents/credentials/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason ?? null }),
    }),

  /** Only the label is editable; client_id and the secret are identity. */
  updateCredential: (id: string, name: string) =>
    request<Credential>(`/agents/credentials/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  deleteCredential: (id: string) =>
    request<CredentialDeleted>(`/agents/credentials/${id}`, { method: 'DELETE' }),
};

/**
 * Allowance at or above which an hour figure is an "unlimited" sentinel rather
 * than a number worth printing. Mirrors `UNLIMITED_HOURS` in the API catalogue.
 */
export const UNLIMITED_HOURS = 1_000_000;

/** Hour figures come off subscription rows too, where there is no flag to read. */
export function formatHours(hours: number): string {
  return hours >= UNLIMITED_HOURS ? 'Unlimited' : String(hours);
}

/** Formats minor units as currency. `null` means the plan is quote-only. */
export function formatPrice(minorUnits: number | null, currency = 'USD'): string {
  if (minorUnits === null) return 'Contact Sales';
  if (minorUnits === 0) return '$0';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
  }).format(minorUnits / 100);
}
